import * as React from 'react'

import type { Control, FieldValues, UseControllerProps } from 'react-hook-form'

import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { cn } from '@/infra'

interface TextareaProps extends React.ComponentProps<'textarea'> {
  containerClassName?: string
}

function Textarea({ className, containerClassName, maxLength = 1000, ...props }: Readonly<TextareaProps>) {
  const counterRef = React.useRef<HTMLSpanElement>(null)

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (counterRef.current) counterRef.current.textContent = `${e.target.value.length}/${maxLength}`
    props.onChange?.(e)
  }

  return (
    <div className={cn('relative inline-block', containerClassName)}>
      <textarea
        data-slot="textarea"
        className={cn(
          'border-input focus:border-primary aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
          'aria-invalid:border-destructive dark:bg-input/30 flex field-sizing-content min-h-16',
          'w-full rounded-md border bg-neutral-50 px-3 py-2 text-base text-neutral-600',
          'shadow-xs transition-[color,box-shadow] outline-none placeholder:text-neutral-500',
          'disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...props}
        onChange={handleChange}
        maxLength={maxLength}
      />
      <span ref={counterRef} className="pointer-events-none absolute right-0 -bottom-5 text-xs text-neutral-500">
        {`${String(props.value ?? '').length}/${maxLength}`}
      </span>
    </div>
  )
}

type TTextareaFieldProps<
  TFieldValues extends FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
> = TextareaProps &
  Omit<UseControllerProps<TFieldValues>, 'control'> & {
    control?: Control<TFieldValues, TContext, TTransformedValues>
    label: string
    classNameLabel?: string
  }

const TextareaField = <TFieldValues extends FieldValues, TContext = unknown, TTransformedValues = TFieldValues>({
  control,
  label,
  classNameLabel,
  ...props
}: TTextareaFieldProps<TFieldValues, TContext, TTransformedValues>) => {
  return (
    <FormField
      name={props.name}
      control={control}
      render={({ field }) => (
        <FormItem>
          <FormLabel className={cn('text-sm font-medium text-neutral-800', classNameLabel)}>{label}</FormLabel>
          <FormControl>
            <Textarea {...field} {...props} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

export { Textarea, TextareaField }
